/*
 * Proton upsell suite — one asset, three surfaces. Source of truth: ~/proton/public/storefront/proton-upsell.js
 * Deploy: node ~/proton/scripts/_deploy-proton-upsell.js
 *
 *  1) GEAR MARQUEE — every paddle PDP (type "paddle" via ShopifyAnalytics.meta, incl. Coyote):
 *     an auto-scrolling rail of pickleball gear (grips, overgrips, RESET cleaner, accessories)
 *     plus the Coyote tee & hat, with one-tap add for single-variant products.
 *  2) COYOTE COLLECTION TEASER — Coyote PDP only: real bundle imagery + prices in the empty
 *     media column, thumbnails grafted into the "See the collection" tile.
 *  3) CHECKOUT UPSELL — cart page + cart-notification: when the cart holds the Coyote paddle
 *     without the tee/hat, "Check out" first offers the REAL bundle products ($215/$220/$235,
 *     component-linked) by swapping the paddle line for the bundle line. Savings copy is the
 *     locked "$N less than buying them separately" formula, N DERIVED from live prices at
 *     runtime — never typed. If a bundle fetch fails, the claim is omitted, never guessed.
 *
 *  Copy rules honoured: no "save $N", no percentages, no code talk, no ship-window text,
 *  no stock counts. All prices come from /products/<handle>.js live.
 *  ⚠ "no struck prices" was RELAXED 2026-09-09 by Diego for the bundle cards ONLY: the offer
 *  now reads "+$20" with the piece's live standalone "$30" struck beside it. Both figures are
 *  CURRENT prices (an upgrade delta vs today's shelf price), so this is the same itemised
 *  comparison as the locked "$N less than buying them separately" line — NOT a former-price
 *  claim, and NOT the banned "save $N". Do not extend struck prices to the paddle itself.
 */
(function () {
  "use strict";
  if (window.__protonUpsell) return;
  window.__protonUpsell = 1;

  var COYOTE = "project-coyote"; // renamed from project-coyote-preorder 2026-09-10 (deploy AFTER the product rename lands)
  var TEE = "project-coyote-tee";
  var CAP = "the-project-coyote-cap";
  var B_TEE = "coyote-bundle-tee";
  var B_CAP = "coyote-bundle-hat";
  var B_KIT = "coyote-bundle-tee-hat";
  // 🎒 THE BAG (2026-09-22, Diego: "should be showing the add-to-cart for a discount if they
  // add the bag now versus separately"). The backpack shipped $95 standalone and the Coyote
  // Kit — paddle + backpack, ONE component-linked product at $275 — shipped 09-21, both AFTER
  // this file last knew the ladder. So the checkout intercept was offering a tee and a hat to
  // a Coyote cart and never the bag, and the $15 kit gap was invisible at the one moment the
  // shopper is deciding. B_BAG is the kit (pickerless, single variant); B_FULL already carries
  // the bag alongside the tee and the hat for carts that hold those too.
  var BAG = "atomic-pickleball-backpack";
  var B_BAG = "coyote-kit";          // paddle + backpack, $275 vs $290 apart
  var B_FULL = "coyote-bundle-full"; // paddle + tee + hat + backpack, $315
  var COLLECTION_URL = "/pages/coyote-collection";
  var GEAR_JSON = "/collections/pickle-ball-gear/products.json?limit=50";
  var BUNDLE_HANDLES = [B_TEE, B_CAP, B_KIT, B_FULL, B_BAG];
  // 2026-09-04 (Diego): the $0 Labor Day free gift hat. Shows as the FIRST card in
  // the drawer's Coyote Collection block when it's NOT already in the cart; once
  // added (with _proton_gift) it drops out and shows as a $0 cart line instead.
  var GIFT_HAT = "proton-labor-day-hat-free-gift";
  var GIFT_HAT_PID = 10789771968804;
  var GIFT_PROP = "_proton_gift";

  /* ------------------------------------------------------------------ css */
  var CSS = "" +
".pu-hide{display:none!important}" +
/* ---- shared card plate ---- */
".pu-plate{background:#fff;border-radius:10px;overflow:hidden;display:flex;align-items:center;justify-content:center}" +
".pu-plate img{width:100%;height:100%;object-fit:contain;display:block}" +
/* ---- gear marquee — plain white so it sits with EVERY paddle colourway, not just Coyote ---- */
".pu-rail{margin:28px 0 8px;padding:16px 0 14px;border-radius:16px;background:#fff;border:1px solid #e8e8e8}" +
".pu-rail-hd{padding:0 18px 12px;display:flex;flex-wrap:wrap;align-items:baseline;gap:4px 12px}" +
".pu-eyebrow{font-size:1.05rem;letter-spacing:.16em;text-transform:uppercase;color:#8a8a8a;font-weight:600}" +
".pu-rail-hd h3{margin:0;font-size:1.6rem;color:#121212;letter-spacing:.02em}" +
".pu-rail-hd p{margin:0;flex-basis:100%;font-size:1.15rem;color:#767676}" +
/* marquee clips: REAL scroll containers that auto-roll via JS — grab, swipe and wheel all work */
".pu-track-clip,.pu-dr-clip{overflow-x:auto;scrollbar-width:none;-ms-overflow-style:none;cursor:grab;" +
  "-webkit-mask-image:linear-gradient(90deg,transparent,#000 4%,#000 96%,transparent);mask-image:linear-gradient(90deg,transparent,#000 4%,#000 96%,transparent)}" +
".pu-track-clip::-webkit-scrollbar,.pu-dr-clip::-webkit-scrollbar{display:none}" +
".pu-track-clip.is-dragging,.pu-dr-clip.is-dragging{cursor:grabbing}" +
".pu-track{display:flex;gap:12px;width:max-content;padding:2px 12px}" +
"@media (prefers-reduced-motion:reduce){.pu-track-clip,.pu-dr-clip{-webkit-mask-image:none;mask-image:none;scrollbar-width:thin}}" +
".pu-card{flex:0 0 152px;background:#fff;border:1px solid #e8e8e8;border-radius:12px;padding:10px;display:flex;flex-direction:column;gap:8px;transition:border-color .18s,box-shadow .18s}" +
"@media(min-width:990px){.pu-card{flex-basis:272px;padding:14px}.pu-card h4{font-size:1.3rem}.pu-card .pu-price{font-size:1.4rem}}" +
".pu-card:hover{border-color:#c9c9c9;box-shadow:0 6px 18px rgba(0,0,0,.08)}" +
".pu-card .pu-plate{aspect-ratio:1/1;border:1px solid #f0f0f0}" +
".pu-card h4{margin:0;font-size:1.12rem;line-height:1.3;color:#121212;font-weight:600;min-height:2.9rem;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}" +
".pu-card h4 a{color:inherit;text-decoration:none}" +
".pu-price{font-size:1.2rem;color:#121212;font-weight:700}" +
".pu-add,.pu-opts{margin-top:auto;border-radius:8px;padding:8px 10px;font-size:1.1rem;font-weight:600;letter-spacing:.03em;text-align:center;cursor:pointer;text-decoration:none;display:block}" +
".pu-add{background:#fff;border:1px solid #d6d6d6;color:#121212}" +
".pu-add:hover{background:#121212;border-color:#121212;color:#fff}" +
".pu-add.is-added,.pu-add.is-added:disabled{background:#1d7a3c;border-color:#1d7a3c;color:#fff;opacity:1}" +
".pu-add:disabled{opacity:.55;cursor:default}" +
".pu-opts{background:#fff;border:1px solid #d6d6d6;color:#121212}" +
".pu-opts:hover{background:#121212;border-color:#121212;color:#fff}" +
/* ---- coyote teaser ---- */
/* Selkirk-style "Complete the Kit" (Diego 09-10): light, flat, quick-add cards.
   The dark charcoal panel is DEAD — do not restore it. */
".pu-yote{margin:28px 0 0;padding:22px 0 6px;border-top:1px solid #e3e6ec}" +
".pu-yote-hd h3{margin:0 0 18px;font-size:1.9rem;font-weight:700;color:#0b0b0d;text-align:center}" +
/* 10-12 Diego: ONE scrolling row on every breakpoint (was a 3-col grid that wrapped to 2 rows). */
".pu-yote-row{position:relative}" +
".pu-yote-grid{display:flex;overflow-x:auto;gap:14px;scroll-snap-type:x mandatory;-webkit-overflow-scrolling:touch;padding:2px 2px 10px;scrollbar-width:thin;scrollbar-color:#d8dde6 transparent}" +
".pu-yote-grid::-webkit-scrollbar{height:6px}.pu-yote-grid::-webkit-scrollbar-thumb{background:#d8dde6;border-radius:99px}" +
".pu-kit-card{flex:0 0 calc(30% - 10px);min-width:0;scroll-snap-align:start}" +
"@media(max-width:600px){.pu-kit-card{flex-basis:64%}}" +
".pu-yote-nav{position:absolute;top:34%;z-index:2;width:38px;height:38px;border-radius:50%;border:1px solid #e3e6ec;background:#fff;color:#0b0b0d;font-size:2rem;line-height:1;display:flex;align-items:center;justify-content:center;cursor:pointer;box-shadow:0 4px 14px rgba(11,11,13,.12);padding:0}" +
".pu-yote-nav.prev{left:-12px}.pu-yote-nav.next{right:-12px}" +
".pu-yote-nav[hidden]{display:none}" +
"@media(hover:none),(max-width:600px){.pu-yote-nav{display:none}}" +
".pu-kit-card{position:relative;display:flex;flex-direction:column;gap:8px;background:#fff;border:1px solid #e3e6ec;border-radius:12px;padding:12px}" +
".pu-kit-card .pu-plate{aspect-ratio:1/1}" +
".pu-kit-card h4{margin:0;font-size:1.25rem;color:#0b0b0d;line-height:1.3;font-weight:700}" +
".pu-yote-pr{display:flex;align-items:baseline;gap:8px}" +
".pu-yote-price{font-size:1.45rem;font-weight:800;color:#0b0b0d}" +
".pu-yote-alone{font-size:1.15rem;color:#8a94a8}" +
".pu-yote-total{font-size:1.05rem;color:#8a94a8}" +
".pu-yote-less{font-size:1.1rem;color:#5a6b87}" +
".pu-kit-sel{display:block;width:100%;box-sizing:border-box;padding:7px 9px;border:1px solid #d8dde6;border-radius:6px;background:#fff;color:#0b0b0d;font-family:inherit;font-size:1.2rem;line-height:1.3}" +
".pu-kit-add{margin-top:auto;background:#ff6000;color:#fff;border:none;border-radius:999px;padding:10px 14px;font-family:inherit;font-size:1.25rem;font-weight:700;letter-spacing:.02em;cursor:pointer}" +
".pu-kit-add:hover{background:#e05400}" +
".pu-kit-add[disabled]{opacity:.6;cursor:default}" +
".pu-kit-add.is-added{background:#0b0b0d}" +
".pu-flag{position:absolute;top:10px;right:10px;background:#FF6000;color:#fff;font-size:.95rem;font-weight:800;letter-spacing:.08em;text-transform:uppercase;padding:3px 8px;border-radius:99px}" +
".pu-yote-link{display:block;margin-top:14px;text-align:center;font-size:1.2rem;font-weight:600;color:#0b0b0d;text-decoration:underline;text-underline-offset:3px}" +
/* ---- tile thumbs ---- */
".pu-tile-thumbs{display:flex;gap:6px;margin-top:8px}" +
".pu-tile-thumbs span{width:34px;height:34px;border-radius:50%;background:#fff;border:1px solid rgba(222,202,164,.7);overflow:hidden;display:inline-flex;align-items:center;justify-content:center}" +
".pu-tile-thumbs img{width:88%;height:88%;object-fit:contain}" +
/* ---- checkout modal — clean white, store-native (Diego: the dark/orange one "looks like a virus popup") ---- */
".pu-ov{position:fixed;inset:0;z-index:2147483646;background:rgba(18,18,22,.42);display:flex;align-items:center;justify-content:center;padding:16px;opacity:0;transition:opacity .18s ease}" +
// 🚨 The mobile /cart sticky bar (#pp-sticky-checkout, z-index 2147483000) paints OVER this
// modal's bottom 70px — exactly where "No thanks — continue to checkout" sits — and its tap
// re-clicks button[name=checkout], which the intercept swallows while #pu-ck exists. Measured
// live 2026-09-29 (390×844): elementFromPoint at the skip button = pp-sticky-checkout ⇒ the
// shopper is STUCK. CSS net here; showModal/close also toggle it inline for non-:has browsers.
"body:has(#pu-ck) #pp-sticky-checkout{display:none!important}" +
".pu-ov.is-on{opacity:1}" +
".pu-md{width:100%;max-width:480px;max-height:92vh;max-height:92dvh;overflow:auto;overscroll-behavior:contain;box-sizing:border-box;border-radius:16px;padding:24px 22px 14px;position:relative;background:#fff;border:1px solid #ececec;" +
  "box-shadow:0 24px 64px rgba(0,0,0,.18);transform:translateY(12px);transition:transform .18s ease}" +
".pu-ov.is-on .pu-md{transform:none}" +
"@media(max-width:600px){.pu-ov{align-items:flex-end;padding:0}.pu-md{border-radius:16px 16px 0 0;max-width:none;padding-bottom:calc(14px + env(safe-area-inset-bottom))}}" +
".pu-x{position:absolute;top:12px;right:14px;background:none;border:0;color:#9a9a9a;font-size:2rem;line-height:1;cursor:pointer;padding:6px}" +
".pu-x:hover{color:#121212}" +
".pu-md-eyebrow{font-size:1rem;letter-spacing:.16em;text-transform:uppercase;color:#8a8a8a;font-weight:600;margin:0 0 4px}" +
".pu-md h3{margin:0 0 16px;font-size:1.9rem;color:#121212;line-height:1.15;font-weight:700}" +
".pu-offer{position:relative;display:grid;grid-template-columns:72px 1fr;gap:12px;background:#fff;border:1px solid #e8e8e8;border-radius:12px;padding:12px;margin-bottom:10px}" +
".pu-offer .pu-plate{width:72px;height:72px;border:1px solid #f0f0f0}" +
".pu-offer h4{margin:0 0 2px;font-size:1.25rem;color:#121212;font-weight:600}" +
".pu-offer-price{font-size:1.3rem;font-weight:700;color:#121212}" +
".pu-offer-less{font-size:1.05rem;color:#767676;margin:1px 0 9px}" +
".pu-offer-row{display:flex;flex-wrap:wrap;gap:8px;align-items:center}" +
".pu-sel{background:#fff;border:1px solid #d6d6d6;border-radius:8px;padding:7px 8px;font-size:1.1rem;min-width:92px;color:#121212}" +
".pu-go{background:#fff;border:1px solid #FF6000;color:#121212;border-radius:8px;padding:9px 14px;font-size:1.12rem;font-weight:600;cursor:pointer;flex:1}" +
".pu-go:hover{background:#FF6000;border-color:#FF6000;color:#fff}" +
".pu-go:disabled{opacity:.5;cursor:default}" +
".pu-skip{display:block;width:100%;text-align:center;background:#FFE7D6;border:1px solid #FF6000;border-radius:10px;color:#7A2E00;font-weight:600;font-size:1.12rem;padding:12px 0;margin-top:6px;cursor:pointer;text-decoration:underline;" +
  // 🚨 2026-10-06: STICKY at the sheet's bottom, above the offer cards. With 4 offers the
  // sheet scrolls and offer #4 sat over/under this button on a 390px iPhone — the tap never
  // landed. Sticky + z-index keeps the exit always on screen and always the hit target.
  "position:sticky;bottom:0;z-index:3;box-shadow:0 10px 0 6px #fff,0 -12px 16px 2px rgba(255,255,255,.96)}" +
".pu-skip:hover{background:#FFD8BE;color:#5A2100}" +
".pu-err{color:#b3261e;font-size:1.02rem;margin:6px 0 0;display:none}" +
/* ---- drawer dock-right fix: this theme's component-cart-drawer.css never shipped the
        stock Dawn `margin-left:auto`, so the panel opened off the LEFT edge. Dock it. ---- */
"cart-drawer.drawer .drawer__inner{margin-left:auto!important;margin-right:0!important;transform:translateX(100%)!important}" +
"cart-drawer.drawer.active .drawer__inner{transform:translateX(0)!important}" +
"cart-drawer.drawer.animate .drawer__inner{transition:transform .35s cubic-bezier(.25,.46,.45,.94)!important}" +
"@media(max-width:480px){cart-drawer.drawer .drawer__inner{width:100vw!important;max-width:100vw!important}}" +
/* ---- in-drawer "You may also like" rail (light, JOOLA-clean) ---- */
".pu-dr{padding:14px 0 4px;border-top:1px solid rgba(18,18,18,.08)}" +
".pu-dr-hd{margin:0 0 10px;font-size:1.1rem;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:#121212}" +
".pu-dr-track{display:flex;gap:10px;width:max-content;padding-bottom:4px}" +
".pu-dr-card{flex:0 0 128px;display:flex;flex-direction:column;gap:6px}" +
".pu-dr-card .pu-plate{aspect-ratio:1/1;border:1px solid rgba(18,18,18,.08);border-radius:10px;background:#f7f7f7}" +
".pu-dr-name{font-size:1.05rem;line-height:1.3;color:#121212;min-height:2.7rem;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;text-decoration:none}" +
".pu-dr-price{font-size:1.1rem;color:#121212;font-weight:600}" +
".pu-dr-add,.pu-dr-choose{border:1px solid #d6d6d6;background:#fff;color:#121212;border-radius:7px;padding:7px 0;font-size:1.05rem;font-weight:600;letter-spacing:.03em;cursor:pointer;text-align:center;text-decoration:none;display:block}" +
".pu-dr-add:hover,.pu-dr-choose:hover{background:#121212;border-color:#121212;color:#fff}" +
/* 2026-09-10 (Diego): "make this checkout button match the same button as the black
   checkout button" — the PDP's Add-to-cart went house-blue -> orange -> CHARCOAL the
   same day (proton-conversion-polish.css, .product-form__submit #0b0b0d/hover #000).
   The drawer's Check out was still on the OLD orange lockup from before that chain —
   match it exactly so the buy button and the checkout button read as one language. */
"cart-drawer .cart__checkout-button{background:#0b0b0d!important;border:1px solid #0b0b0d!important;color:#fff!important;box-shadow:none!important}" +
"cart-drawer .cart__checkout-button:hover{background:#000!important;border-color:#000!important}" +
"cart-drawer .cart__checkout-button:after,cart-drawer .cart__checkout-button:before{box-shadow:none!important;background:none!important}" +
".pu-dr-add.is-added,.pu-dr-add.is-added:disabled{background:#1d7a3c;border-color:#1d7a3c;color:#fff;opacity:1}" +
".pu-dr-add:disabled{opacity:.5;cursor:default}" +
/* ---- in-drawer Coyote Collection offers (above "You may also like") — clean, store-native ---- */
".pu-dy{padding:12px 0 4px;border-top:1px solid rgba(18,18,18,.08)}" +
".pu-dy-hd{margin:0 0 10px;font-size:1.1rem;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:#121212}" +
/* horizontal scroll wheel — cards side by side, swipe/scroll to see more (saves vertical space) */
".pu-dy-track{display:flex;gap:10px;overflow-x:auto;scrollbar-width:none;-ms-overflow-style:none;padding-bottom:4px;scroll-snap-type:x proximity}" +
".pu-dy-track::-webkit-scrollbar{display:none}" +
".pu-dy-card{flex:0 0 188px;scroll-snap-align:start;border:1px solid #e8e8e8;border-radius:11px;padding:10px;display:flex;flex-direction:column;gap:3px;background:#fff}" +
".pu-dy-card .pu-plate{width:100%;aspect-ratio:1/1;border:1px solid #f0f0f0;border-radius:8px}" +
".pu-dy-card h4{margin:1px 0 0;font-size:1.1rem;color:#121212;font-weight:600;line-height:1.25}" +
".pu-dy-price{font-size:1.15rem;font-weight:700;color:#121212}" +
/* struck standalone price sits BESIDE the "+$N" delta, never replacing it */
".pu-dy-priceline,.pu-offer-priceline{display:flex;align-items:baseline;gap:6px;flex-wrap:wrap}" +
".pu-dy-was,.pu-offer-was{color:#8a8a8a;font-weight:500;text-decoration:line-through}" +
".pu-dy-was{font-size:1.05rem}" +
".pu-offer-was{font-size:1.2rem}" +
".pu-dy-price.pu-dy-free{color:#FF6000;letter-spacing:.04em}" +
".pu-dy-card.pu-dy-gift{border-color:rgba(255,96,0,.55);box-shadow:0 8px 22px rgba(255,96,0,.16)}" +
".pu-dy-less{display:block;font-size:.98rem;color:#767676;margin:0;line-height:1.25}" +
/* controls follow the copy — no margin-top:auto: it parked them at the card floor and the
   dead space pushed "You may also like" below the fold */
/* 🚨 margin-top:AUTO, not 5px. The bag card is PICKERLESS (single variant → no <select>), so
   with a fixed top margin its Add button floated mid-card while the Tee's sat at the bottom —
   two CTAs on different baselines in one scroll track. auto pins every CTA to the card floor
   whatever controls sit above it. */
".pu-dy-ctl{display:flex;flex-direction:column;gap:5px;margin-top:auto;padding-top:5px}" +
".pu-dy-ctl .pu-sel{width:100%;padding:6px 6px;font-size:1.02rem}" +
".pu-dy-go{width:100%;background:#fff;border:1px solid #FF6000;color:#121212;border-radius:7px;padding:7px 10px;font-size:1.05rem;font-weight:600;cursor:pointer}" +
".pu-dy-go:hover{background:#FF6000;border-color:#FF6000;color:#fff}.pu-dy-go:disabled{opacity:.5;cursor:default}" +
".pu-dy .pu-err{color:#b3261e}";

  function injectCSS() {
    if (document.getElementById("pu-style")) return;
    var s = document.createElement("style");
    s.id = "pu-style";
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  /* --------------------------------------------------------------- utils */
  function fetchJSON(url) {
    return fetch(url, { credentials: "same-origin", headers: { Accept: "application/json" } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }
  var _prodCache = {};
  function product(handle) {
    if (!_prodCache[handle]) _prodCache[handle] = fetchJSON("/products/" + handle + ".js");
    return _prodCache[handle];
  }
  function getCart() { return fetchJSON("/cart.js"); }
  // /cart.js can get rate-limited; a silent null must never kill the offers.
  function getCartRetry(tries) {
    return getCart().then(function (c) {
      if (c || !tries) return c;
      return new Promise(function (res) { setTimeout(res, 1600); }).then(function () { return getCartRetry(tries - 1); });
    });
  }
  // Last-resort cart read: the drawer's own line items name their products in the DOM.
  // Handles only — enough for eligibility/display; swaps re-fetch the real cart at click.
  function domCart(drawer) {
    if (!drawer) return null;
    var items = [];
    drawer.querySelectorAll(".cart-item a[href*='/products/']").forEach(function (a) {
      var m = (a.getAttribute("href") || "").match(/\/products\/([a-z0-9\-]+)/);
      if (m && !items.some(function (i) { return i.handle === m[1]; })) items.push({ handle: m[1] });
    });
    return items.length ? { items: items, _dom: true } : null;
  }
  function money(cents) {
    if (typeof cents !== "number") return "";
    var d = cents / 100;
    return "$" + (d % 1 === 0 ? d.toFixed(0) : d.toFixed(2));
  }
  function imgUrl(u, w) {
    if (!u) return "";
    u = String(u);
    if (u.slice(0, 2) === "//") u = "https:" + u;
    return u + (u.indexOf("?") > -1 ? "&" : "?") + "width=" + (w || 360);
  }
  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }
  function currentHandle() {
    var m = location.pathname.match(/\/products\/([a-z0-9\-]+)/);
    return m ? m[1] : null;
  }
  function isPaddlePDP() {
    try {
      var m = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
      if (m && m.product && typeof m.product.type === "string") return m.product.type === "paddle";
    } catch (e) {}
    return false;
  }
  function addToCart(items) {
    return fetch("/cart/add.js", {
      method: "POST", credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ items: items })
    }).then(function (r) { if (!r.ok) throw new Error("add " + r.status); return r.json(); });
  }
  function changeLine(key, qty) {
    return fetch("/cart/change.js", {
      method: "POST", credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: key, quantity: qty })
    }).then(function (r) { if (!r.ok) throw new Error("change " + r.status); return r.json(); });
  }
  function refreshBubble() {
    fetch("/?sections=cart-icon-bubble", { credentials: "same-origin" })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        if (!j || !j["cart-icon-bubble"]) return;
        var host = document.getElementById("cart-icon-bubble");
        if (!host) return;
        var tmp = document.createElement("div");
        tmp.innerHTML = j["cart-icon-bubble"];
        var inner = tmp.querySelector("#cart-icon-bubble") || tmp.firstElementChild;
        if (inner) host.innerHTML = inner.innerHTML;
      }).catch(function () {});
  }
  // Re-render the theme's cart drawer from fresh section HTML and slide it open —
  // the JOOLA flow: add → the bag appears on the right with the cart + recommendations.
  function refreshDrawerAndOpen(open) {
    var drawer = document.querySelector("cart-drawer");
    if (!drawer) { refreshBubble(); return Promise.resolve(false); }
    return fetch("/?sections=cart-drawer,cart-icon-bubble", { credentials: "same-origin" })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        if (!j) return false;
        if (j["cart-drawer"]) {
          var tmp = document.createElement("div");
          tmp.innerHTML = j["cart-drawer"];
          var freshInner = tmp.querySelector("#CartDrawer");
          var liveInner = drawer.querySelector("#CartDrawer");
          if (freshInner && liveInner) liveInner.innerHTML = freshInner.innerHTML;
          var freshDrawer = tmp.querySelector("cart-drawer");
          if (freshDrawer) drawer.classList.toggle("is-empty", freshDrawer.classList.contains("is-empty"));
        }
        if (j["cart-icon-bubble"]) {
          var host = document.getElementById("cart-icon-bubble");
          if (host) {
            var t2 = document.createElement("div");
            t2.innerHTML = j["cart-icon-bubble"];
            var inner = t2.querySelector("#cart-icon-bubble") || t2.firstElementChild;
            if (inner) host.innerHTML = inner.innerHTML;
          }
        }
        if (open) {
          try { if (typeof drawer.open === "function") drawer.open(); else drawer.classList.add("animate", "active"); }
          catch (e) { drawer.classList.add("animate", "active"); }
        }
        return true;
      }).catch(function () { return false; });
  }

  // "$N less than buying them separately" — N derived from live figures, never typed.
  function lessLine(bundleCents, partsCents) {
    var n = partsCents - bundleCents;
    if (!(n > 0)) return "";
    return money(n) + " less than buying them separately";
  }
  function optionValues(prod, nameRe) {
    var opt = (prod.options || []).find ? (prod.options || []).find(function (o) { return nameRe.test(o.name || o); }) : null;
    if (opt && opt.values) return opt.values;
    // .js options can be plain strings; derive values off variants
    var idx = (prod.options || []).map(function (o) { return o.name || o; }).findIndex(function (n) { return nameRe.test(n); });
    if (idx < 0) return [];
    var seen = [], out = [];
    (prod.variants || []).forEach(function (v) {
      var val = v.options ? v.options[idx] : [v.option1, v.option2, v.option3][idx];
      if (val && seen.indexOf(val) < 0) { seen.push(val); out.push(val); }
    });
    return out;
  }
  function matchVariant(prod, wants) {
    return (prod.variants || []).filter(function (v) {
      var opts = v.options || [v.option1, v.option2, v.option3].filter(Boolean);
      return wants.every(function (w) { return opts.indexOf(w) > -1; });
    })[0] || null;
  }

  /* ---------------------------------------------------------------------------
     Auto-rolling marquee on a REAL scroll container: rolls on its own, but the
     shopper can grab (mouse drag), swipe or wheel at any time — interaction
     pauses the roll, it resumes after a beat. Content is duplicated ×2 and the
     scroll position wraps at the halfway point, both while rolling and while
     the user scrolls, so there is no visible end. Honours prefers-reduced-motion.
  --------------------------------------------------------------------------- */
  function makeMarquee(clip) {
    if (!clip || clip._puMq) return;
    clip._puMq = 1;
    var track = clip.firstElementChild;
    if (!track) return;
    var wrap = function () {
      var half = track.scrollWidth / 2;
      if (half > 0 && clip.scrollLeft >= half) clip.scrollLeft -= half;
    };
    clip.addEventListener("scroll", wrap, { passive: true });
    if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) return; // scrollable, no auto-roll
    var paused = false, resumeT = null, last = null, carry = 0, dragging = false, dragX = 0;
    var pause = function (ms) {
      paused = true;
      last = null;
      if (resumeT) clearTimeout(resumeT);
      if (ms > 0) resumeT = setTimeout(function () { paused = false; }, ms);
    };
    clip.addEventListener("pointerenter", function (e) { if (e.pointerType === "mouse") pause(0); });
    clip.addEventListener("pointerleave", function (e) { dragging = false; clip.classList.remove("is-dragging"); if (e.pointerType === "mouse") pause(1200); });
    clip.addEventListener("wheel", function () { pause(3000); }, { passive: true });
    clip.addEventListener("touchstart", function () { pause(3500); }, { passive: true });
    clip.addEventListener("touchend", function () { pause(3500); }, { passive: true });
    clip.addEventListener("pointerdown", function (e) {
      pause(3500);
      if (e.pointerType === "mouse") { dragging = true; dragX = e.clientX; clip.classList.add("is-dragging"); }
    });
    clip.addEventListener("pointermove", function (e) {
      if (dragging) { clip.scrollLeft -= (e.clientX - dragX); dragX = e.clientX; }
    });
    window.addEventListener("pointerup", function () { dragging = false; clip.classList.remove("is-dragging"); });
    var step = function (ts) {
      if (!clip.isConnected) return; // detached by a drawer re-render — stop this loop
      if (!paused) {
        if (last != null) {
          carry += Math.min(ts - last, 100) * 0.035; // ~35px/s, capped after tab-sleep
          var px = carry | 0;
          if (px) { clip.scrollLeft += px; carry -= px; wrap(); }
        }
        last = ts;
      }
      requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  /* ==================================================== 1. GEAR MARQUEE */
  function buildMarquee(mount, excludeHandle) {
    Promise.all([fetchJSON(GEAR_JSON), product(TEE), product(CAP)]).then(function (res) {
      var gear = (res[0] && res[0].products) || [];
      var items = [];
      gear.forEach(function (p) {
        if (p.handle === excludeHandle) return;
        var avail = (p.variants || []).some(function (v) { return v.available; });
        if (!avail) return;
        var v0 = (p.variants || []).filter(function (v) { return v.available; })[0];
        items.push({
          handle: p.handle, title: p.title,
          img: (p.images && p.images[0] && p.images[0].src) || "",
          priceCents: Math.round(parseFloat(v0.price) * 100),
          addId: (p.variants || []).length === 1 ? v0.id : null
        });
      });
      [res[1], res[2]].forEach(function (p) {
        if (!p || !p.available) return;
        items.push({
          handle: p.handle, title: p.title,
          img: p.featured_image || (p.images && p.images[0]) || "",
          priceCents: p.price,
          addId: null // multi-variant → choose on PDP
        });
      });
      if (items.length < 3) return;

      var cards = items.map(function (it) {
        var btn = it.addId
          ? "<button type='button' class='pu-add' data-vid='" + it.addId + "'>+ Add</button>"
          : "<a class='pu-opts' href='/products/" + esc(it.handle) + "'>Choose &rsaquo;</a>";
        return "<div class='pu-card'>" +
          "<a class='pu-plate' href='/products/" + esc(it.handle) + "' tabindex='-1'><img loading='lazy' src='" + esc(imgUrl(it.img, 300)) + "' alt='" + esc(it.title) + "'></a>" +
          "<h4><a href='/products/" + esc(it.handle) + "'>" + esc(it.title) + "</a></h4>" +
          "<span class='pu-price'>" + money(it.priceCents) + "</span>" + btn + "</div>";
      }).join("");

      var el = mount;
      el.className = "pu-rail";
      el.id = "pu-rail";
      el.innerHTML =
        "<div class='pu-rail-hd'><span class='pu-eyebrow'>Pickleball gear</span><h3>Complete your setup</h3>" +
        "<p>Grips, overgrips, paddle care and more &mdash; add them alongside your paddle.</p></div>" +
        "<div class='pu-track-clip'><div class='pu-track'>" + cards + cards + "</div></div>";
      makeMarquee(el.querySelector(".pu-track-clip"));

      el.addEventListener("click", function (e) {
        var btn = e.target.closest && e.target.closest(".pu-add");
        if (!btn || btn.disabled) return;
        btn.disabled = true;
        btn.textContent = "Adding…";
        addToCart([{ id: Number(btn.getAttribute("data-vid")), quantity: 1 }]).then(function () {
          // mirror this product's button in BOTH halves of the looped track
          el.querySelectorAll(".pu-add[data-vid='" + btn.getAttribute("data-vid") + "']").forEach(function (b) {
            b.classList.add("is-added"); b.textContent = "Added ✓"; b.disabled = true;
          });
          refreshDrawerAndOpen(true); // JOOLA flow: the bag slides in with the cart + recs
        }).catch(function () {
          btn.disabled = false; btn.textContent = "+ Add";
        });
      });
    });
  }

  /* ============================================ 2. COYOTE COLLECTION TEASER */
  function buildCoyoteTeaser(mount) {
    Promise.all([product(COYOTE), product(TEE), product(CAP), product(B_TEE), product(B_CAP), product(B_KIT),
                 product(BAG), product(B_BAG)])
      .then(function (r) {
        var pad = r[0], tee = r[1], cap = r[2], bag = r[6];
        // DELTA framing (Diego 09-10): the shopper is anchored on the paddle's
        // $195 — cards price the ADD ("+$20"), not the bundle total, with the
        // item-alone price struck through and the real total in small print.
        // Everything is DERIVED at runtime; if the paddle fetch fails we fall
        // back to absolute bundle prices rather than typing a number.
        var defs = [
          // 🎒 The bag leads the PDP rail for the same reason it leads the checkout modal:
          // it is the real companion to a paddle, and its gap ($15) is the widest on the
          // ladder. "Best value" below is DERIVED, so it lands on this card on its own.
          { b: r[7], label: "Add the Backpack", parts: pad && bag ? pad.price + bag.price : null,
            alone: bag ? bag.price : null, img: bag && bag.featured_image },
          { b: r[3], label: "Add the Tee", parts: pad && tee ? pad.price + tee.price : null,
            alone: tee ? tee.price : null, img: tee && tee.featured_image },
          { b: r[4], label: "Add the Hat", parts: pad && cap ? pad.price + cap.price : null,
            alone: cap ? cap.price : null, img: cap && cap.featured_image },
          { b: r[5], label: "Add Both — The Kit", parts: pad && tee && cap ? pad.price + tee.price + cap.price : null,
            alone: tee && cap ? tee.price + cap.price : null, img: null }
        ].filter(function (d) { return d.b && d.b.available; });
        if (!defs.length) return;

        // "Best value" is DERIVED: the live tier with the biggest gap, ties → no flag.
        var best = -1, bestGap = 0, tie = false;
        defs.forEach(function (d, i) {
          var g = d.parts != null ? d.parts - d.b.price : 0;
          if (g > bestGap) { bestGap = g; best = i; tie = false; }
          else if (g === bestGap && g > 0) tie = true;
        });
        if (tie) best = -1;

        // Selkirk-style cards (Diego 09-10): white plate, name, price, the locked
        // "$N less than buying them separately" line, per-option dropdowns and an
        // orange Quick Add that puts the REAL bundle variant in the cart.
        var cards = defs.map(function (d, i) {
          var less = d.parts != null ? lessLine(d.b.price, d.parts) : "";
          var sels = (d.b.options || []).map(function (o, oi) {
            var vals = o.values || [];
            if (vals.length < 2 && (vals[0] === "Default Title" || !vals.length)) return "";
            return "<select class='pu-kit-sel' data-card='" + i + "' data-opt='" + oi + "' aria-label='" + esc(o.name) + "'>" +
              vals.map(function (v) { return "<option value=\"" + esc(v) + "\">" + esc(o.name) + ": " + esc(v) + "</option>"; }).join("") +
              "</select>";
          }).join("");
          var delta = pad ? d.b.price - pad.price : null;
          var priceRow = delta != null
            ? "<span class='pu-yote-price'>+" + money(delta) + "</span>" +
              (d.alone != null && d.alone > delta ? "<s class='pu-yote-alone'>" + money(d.alone) + " alone</s>" : "")
            : "<span class='pu-yote-price'>" + money(d.b.price) + "</span>";
          var total = delta != null ? "<span class='pu-yote-total'>" + money(d.b.price) + " total with the paddle</span>" : "";
          return "<div class='pu-kit-card'>" +
            (i === best ? "<span class='pu-flag'>Best value</span>" : "") +
            "<a class='pu-plate' href='" + COLLECTION_URL + "' tabindex='-1'><img loading='lazy' src='" + esc(imgUrl(d.img || d.b.featured_image || "", 420)) + "' alt='" + esc(d.b.title) + "'></a>" +
            "<h4>" + esc(d.label) + "</h4>" +
            "<div class='pu-yote-pr'>" + priceRow + "</div>" +
            (less ? "<span class='pu-yote-less'>" + esc(less) + "</span>" : "") +
            total +
            sels +
            "<button type='button' class='pu-kit-add' data-card='" + i + "'>Quick Add</button>" +
            "</div>";
        }).join("");

        mount.className = "pu-yote";
        mount.id = "pu-yote";
        mount.innerHTML =
          "<div class='pu-yote-hd'><h3>Complete the Kit</h3></div>" +
          "<div class='pu-yote-row'><button type='button' class='pu-yote-nav prev' aria-label='Scroll left' hidden>&lsaquo;</button>" +
          "<div class='pu-yote-grid'>" + cards + "</div>" +
          "<button type='button' class='pu-yote-nav next' aria-label='Scroll right'>&rsaquo;</button></div>" +
          "<a class='pu-yote-link' href='" + COLLECTION_URL + "'>Shop the Coyote Collection &rsaquo;</a>";

        (function () {
          var g = mount.querySelector(".pu-yote-grid"), pv = mount.querySelector(".pu-yote-nav.prev"), nx = mount.querySelector(".pu-yote-nav.next");
          if (!g || !pv || !nx) return;
          function sync() { pv.hidden = g.scrollLeft < 4; nx.hidden = g.scrollLeft + g.clientWidth >= g.scrollWidth - 4; }
          function go(dir) { var c = g.querySelector(".pu-kit-card"); g.scrollBy({ left: dir * ((c ? c.offsetWidth : 200) + 14), behavior: "smooth" }); }
          pv.addEventListener("click", function () { go(-1); });
          nx.addEventListener("click", function () { go(1); });
          g.addEventListener("scroll", sync, { passive: true });
          window.addEventListener("resize", sync);
          setTimeout(sync, 0);
        })();

        mount.addEventListener("click", function (e) {
          var btn = e.target.closest && e.target.closest(".pu-kit-add");
          if (!btn || btn.disabled) return;
          var i = Number(btn.getAttribute("data-card"));
          var b = defs[i] && defs[i].b;
          if (!b) return;
          var chosen = {};
          mount.querySelectorAll(".pu-kit-sel[data-card='" + i + "']").forEach(function (sel) {
            chosen[Number(sel.getAttribute("data-opt"))] = sel.value;
          });
          var v = (b.variants || []).find(function (vr) {
            return vr.available &&
              (chosen[0] == null || vr.option1 === chosen[0]) &&
              (chosen[1] == null || vr.option2 === chosen[1]) &&
              (chosen[2] == null || vr.option3 === chosen[2]);
          });
          if (!v) { btn.textContent = "Unavailable"; return; }
          btn.disabled = true;
          btn.textContent = "Adding…";
          // The bundle CONTAINS the paddle — if a loose paddle is already in the
          // cart, decrement it so quick-adding a bundle never double-paddles.
          addToCart([{ id: v.id, quantity: 1 }]).then(function () {
            if (!pad) return null;
            return fetch("/cart.js", { credentials: "same-origin" })
              .then(function (r) { return r.ok ? r.json() : null; })
              .then(function (c) {
                var loose = c && c.items.find(function (it) { return it.product_id === pad.id; });
                if (loose) return changeLine(loose.key, loose.quantity - 1);
              })
              .catch(function () {});
          }).then(function () {
            btn.classList.add("is-added");
            btn.textContent = "Added ✓";
            refreshDrawerAndOpen(true);
          }).catch(function () {
            btn.disabled = false;
            btn.textContent = "Quick Add";
          });
        });

        // graft thumbnails into the "See the collection" tile so it reads as products, not a button
        var tile = document.querySelector("a.pp-collection");
        if (tile && !tile.querySelector(".pu-tile-thumbs") && tee && cap) {
          var span = tile.querySelector("span") || tile;
          var thumbs = document.createElement("span");
          thumbs.className = "pu-tile-thumbs";
          [tee.featured_image, cap.featured_image, r[5] && r[5].featured_image].forEach(function (u) {
            if (!u) return;
            var w = document.createElement("span");
            w.innerHTML = "<img loading='lazy' src='" + esc(imgUrl(u, 80)) + "' alt=''>";
            thumbs.appendChild(w);
          });
          span.appendChild(thumbs);
        }
      });
  }

  /* ============================================ 2b. IN-DRAWER "YOU MAY ALSO LIKE" */
  function railItems() {
    if (railItems._p) return railItems._p;
    railItems._p = Promise.all([fetchJSON(GEAR_JSON), product(TEE), product(CAP)]).then(function (res) {
      var items = [];
      ((res[0] && res[0].products) || []).forEach(function (p) {
        var live = (p.variants || []).filter(function (v) { return v.available; });
        if (!live.length) return;
        items.push({
          handle: p.handle, title: p.title, productId: p.id, pb: /grip|pickleball|paddle/i.test((p.product_type || "") + " " + p.title),
          img: (p.images && p.images[0] && p.images[0].src) || "",
          priceCents: Math.round(parseFloat(live[0].price) * 100),
          addId: (p.variants || []).length === 1 ? live[0].id : null
        });
      });
      [res[1], res[2]].forEach(function (p) {
        if (!p || !p.available) return;
        items.push({
          handle: p.handle, title: p.title, productId: p.id,
          img: p.featured_image || "", priceCents: p.price, addId: null, pb: true
        });
      });
      if (!items.length) railItems._p = null; // an empty/failed sweep must NOT be cached —
      return items;                            // the next drawer open retries fresh
    }).catch(function () {
      railItems._p = null;
      return [];
    });
    return railItems._p;
  }

  function injectDrawerRail() {
    var drawer = document.querySelector("cart-drawer");
    if (!drawer || drawer.querySelector("#pu-dr")) return;
    var inner = drawer.querySelector("#CartDrawer");
    if (!inner || drawer.classList.contains("is-empty")) return;
    Promise.all([railItems(), getCartRetry(2)]).then(function (r) {
      // A rate-limited /cart.js must never blank the offers — fall back to reading the
      // handles off the drawer's own rendered line items.
      if (!r[1]) r[1] = domCart(drawer);
      if (!r[1]) return;
      inner = drawer.querySelector("#CartDrawer"); // re-resolve: the theme may have re-rendered meanwhile
      if (!inner || drawer.querySelector("#pu-dr")) return;
      var footer = inner.querySelector(".drawer__footer");
      var inCart = {};
      ((r[1] && r[1].items) || []).forEach(function (it) { inCart[it.handle] = 1; });
      // 2026-10-08: a BAT-ONLY cart never gets pickleball grips/cleaner/Coyote merch — neutral gear only.
      var cartItems = (r[1] && r[1].items) || [];
      var batOnly = cartItems.length > 0 && cartItems.every(function (it) { return /bat/i.test(it.product_type || ""); });
      var items = r[0].filter(function (it) { return !inCart[it.handle] && !(batOnly && it.pb); }).slice(0, 12);
      if (!items.length) return;
      var cardsHtml = items.map(function (it) {
        var btn = it.addId
          ? "<button type='button' class='pu-dr-add' data-vid='" + it.addId + "'>Quick add</button>"
          : "<a class='pu-dr-choose' href='/products/" + esc(it.handle) + "'>Choose</a>";
        return "<div class='pu-dr-card'>" +
          "<a class='pu-plate' href='/products/" + esc(it.handle) + "' tabindex='-1'><img loading='lazy' src='" + esc(imgUrl(it.img, 240)) + "' alt='" + esc(it.title) + "'></a>" +
          "<a class='pu-dr-name' href='/products/" + esc(it.handle) + "'>" + esc(it.title) + "</a>" +
          "<span class='pu-dr-price'>" + money(it.priceCents) + "</span>" + btn + "</div>";
      }).join("");
      var el = document.createElement("div");
      el.id = "pu-dr";
      el.className = "pu-dr";
      // rolling marquee: track duplicated ×2; auto-rolls AND stays hand-scrollable
      el.innerHTML = "<p class='pu-dr-hd'>You may also like</p>" +
        "<div class='pu-dr-clip'><div class='pu-dr-track'>" + cardsHtml + cardsHtml + "</div></div>";
      // Mount INSIDE the scrollable cart-drawer-items region (after the line items), so the
      // recs scroll with the cart and the pinned footer/Check out stays on screen at EVERY
      // viewport height — the JOOLA layout. (Mounting as a fixed sibling above the footer
      // pushed Check out below the fold on phones.)
      var scroller = inner.querySelector("cart-drawer-items");
      if (scroller) scroller.appendChild(el);
      else if (footer && footer.parentElement) footer.parentElement.insertBefore(el, footer);
      else (inner.querySelector(".drawer__inner") || inner).appendChild(el);
      makeMarquee(el.querySelector(".pu-dr-clip"));

      el.addEventListener("click", function (e) {
        var btn = e.target.closest && e.target.closest(".pu-dr-add");
        if (!btn || btn.disabled) return;
        btn.disabled = true;
        btn.textContent = "Adding…";
        addToCart([{ id: Number(btn.getAttribute("data-vid")), quantity: 1 }]).then(function () {
          // the drawer re-render replaces this whole rail; the observer re-injects it
          return refreshDrawerAndOpen(true);
        }).catch(function () {
          btn.disabled = false;
          btn.textContent = "Quick add";
        });
      });

      injectCoyoteBlock(drawer, r[1]); // Coyote Collection offers sit ABOVE the rail
    });
  }

  // "Anybody has a Coyote? They need to have the Coyote collection" — the shirt, the hat
  // and the kit with their value, directly above "You may also like" in the drawer.
  function injectCoyoteBlock(drawer, cart) {
    if (!cart || drawer.querySelector("#pu-dy")) return;
    var st = cartState(cart);
    if (!eligible(st)) return;
    // 🚨 THE THIRD SURFACE. `offersFor` builds the bag card only when it is HANDED the bag
    // and the kit — so this list is load-bearing, not boilerplate. It was missed on
    // 2026-09-22 and the drawer went on showing only the Tee and the Hat while the checkout
    // modal and the PDP rail led with the Backpack (Diego caught it in the open cart).
    // Any future piece has to be added in ALL THREE fetch lists: here, buildCoyoteTeaser,
    // and bindCheckoutIntercept.
    Promise.all([product(COYOTE), product(TEE), product(CAP), product(B_TEE), product(B_CAP), product(B_KIT), Promise.resolve(null) /* GIFT_HAT: Labor Day hat is DRAFT ⇒ /products/…js 404'd on every drawer render (2026-10-06) */,
                 product(BAG), product(B_BAG), product(B_FULL)])
      .then(function (r) {
        var inner = drawer.querySelector("#CartDrawer");
        if (!inner || drawer.querySelector("#pu-dy")) return;
        var offers = offersFor(st, { pad: r[0], tee: r[1], cap: r[2], bTee: r[3], bCap: r[4], bKit: r[5],
                                     bag: r[7], bBag: r[8], bFull: r[9] });

        // Free gift hat card — FIRST option, only when it isn't already in the cart.
        var gift = r[6];
        var haveGift = (cart.items || []).some(function (it) {
          return it.handle === GIFT_HAT || Number(it.product_id) === GIFT_HAT_PID;
        });
        var gv = gift && gift.variants && gift.variants[0];
        var giftHtml = "";
        if (false /* 2026-09-08 (Diego): Labor Day over — free-hat drawer card off */ && gift && gv && gv.available && !haveGift) {
          var gimg = (gv.featured_image && gv.featured_image.src) || gift.featured_image;
          giftHtml =
            "<div class='pu-dy-card pu-dy-gift' data-gift='" + gv.id + "'>" +
              "<span class='pu-plate'><img loading='lazy' src='" + esc(imgUrl(gimg, 300)) + "' alt=''></span>" +
              "<h4>Free Hat</h4>" +
              "<span class='pu-dy-price pu-dy-free'>FREE</span>" +
              "<span class='pu-dy-less'>with your Coyote</span>" +
              "<div class='pu-dy-ctl'><button type='button' class='pu-dy-go'>Add free hat</button></div>" +
              "<p class='pu-err'></p></div>";
        }

        if (!offers.length && !giftHtml) return;
        var el = document.createElement("div");
        el.id = "pu-dy";
        el.className = "pu-dy";
        el.innerHTML = "<p class='pu-dy-hd'>The Coyote Collection</p><div class='pu-dy-track'>" + giftHtml + offers.map(function (o, i) {
          var sels = o.selects.map(function (s) {
            return "<select class='pu-sel' data-k='" + s.key + "' aria-label='" + esc(s.label) + "'>" +
              s.values.map(function (v) { return "<option value='" + esc(v) + "'>" + esc(v) + "</option>"; }).join("") + "</select>";
          }).join("");
          return "<div class='pu-dy-card' data-i='" + i + "'>" +
            "<span class='pu-plate'><img loading='lazy' src='" + esc(imgUrl(o.pieceImg || o.img, 300)) + "' alt=''></span>" +
            "<h4>" + esc(o.title) + "</h4>" +
            priceHtml(o, "pu-dy") +
            (o.less ? "<span class='pu-dy-less'>" + esc(o.less) + "</span>" : "") +
            "<div class='pu-dy-ctl'>" + sels + "<button type='button' class='pu-dy-go'>Add</button></div>" +
            "<p class='pu-err'></p></div>";
        }).join("") + "</div>";
        var rail = inner.querySelector("#pu-dr");
        var scroller = inner.querySelector("cart-drawer-items");
        var footer = inner.querySelector(".drawer__footer");
        if (rail && rail.parentElement) rail.parentElement.insertBefore(el, rail); // above "You may also like"
        else if (scroller) scroller.appendChild(el);
        else if (footer && footer.parentElement) footer.parentElement.insertBefore(el, footer);
        else (inner.querySelector(".drawer__inner") || inner).appendChild(el);

        el.addEventListener("click", function (e) {
          var btn = e.target.closest && e.target.closest(".pu-dy-go");
          if (!btn || btn.disabled) return;
          var row = btn.closest(".pu-dy-card");

          // Free gift hat: just add the $0 hat (flagged) — no bundle swap.
          var giftId = row.getAttribute("data-gift");
          if (giftId) {
            btn.disabled = true;
            btn.textContent = "Adding…";
            var props = {}; props[GIFT_PROP] = "1";
            addToCart([{ id: Number(giftId), quantity: 1, properties: props }]).then(function () {
              return refreshDrawerAndOpen(true);
            }).catch(function () {
              btn.disabled = false;
              btn.textContent = "Add free hat";
              var ge = row.querySelector(".pu-err");
              if (ge) { ge.style.display = "block"; ge.textContent = "Couldn’t add — try again."; }
            });
            return;
          }

          var o = offers[Number(row.getAttribute("data-i"))];
          var wants = (o.fixed || []).slice();
          row.querySelectorAll(".pu-sel").forEach(function (s) { wants.push(s.value); });
          var v = matchVariant(o.bundle, wants);
          var err = row.querySelector(".pu-err");
          if (!v || !v.available) {
            err.style.display = "block";
            err.textContent = "That combination is unavailable — pick another.";
            return;
          }
          err.style.display = "none";
          btn.disabled = true;
          btn.textContent = "Adding…";
          performSwap(o, v).then(function () {
            return refreshDrawerAndOpen(true); // re-render: cart shows the bundle, this block retires itself
          }).catch(function () {
            btn.disabled = false;
            btn.textContent = "Add";
            err.style.display = "block";
            err.textContent = "Couldn’t update the cart — try again.";
          });
        });
      });
  }

  function initDrawerRail() {
    var drawer = document.querySelector("cart-drawer");
    if (!drawer) return;
    injectDrawerRail();
    var pending = false;
    var kick = function () {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () {
        pending = false;
        injectDrawerRail();
      });
    };
    // childList: the theme re-rendered the drawer. attributes/class: the drawer was
    // (re)OPENED — closing and reopening fires no childList mutation, and the offers
    // must be there EVERY time it opens, not just after an add.
    new MutationObserver(kick).observe(drawer, { childList: true, subtree: true, attributes: true, attributeFilter: ["class"] });
    document.addEventListener("cart:updated", kick);
    document.addEventListener("cart:refresh", kick);
  }

  /* ================================================= 3. CHECKOUT UPSELL */
  var SEEN_KEY = "pu-ck-dismissed";
  var proceeding = false;

  // 🧪 A/B 2026-10-13 -> 2026-10-27 (Diego OK'd 10-13): 50/50 per browser,
  //   "popup" = the Complete-the-kit modal as before, "none" = straight to checkout.
  // Why: mobile checkout completion 18% vs desktop 33%; this modal is the only surface
  // between Check out and the payment page. Arm is written to the CART as attribute
  // `puck_ab`, so orders (note_attributes) AND abandoned checkouts (customAttributes)
  // both carry it => readout: node ~/proton/scripts/puck-ab/readout.js
  // QA override: ?puck=popup | ?puck=none (sticks to this browser).
  // After CK_AB_END everyone gets the popup again until a decision is made.
  var CK_AB_END = Date.UTC(2026, 9, 27, 7, 0, 0); // 2026-10-27 00:00 Arizona
  var CK_AB_LIVE = Date.now() < CK_AB_END;
  var CK_ARM = "popup";
  try {
    var _q = (location.search.match(/[?&]puck=(popup|none)\b/) || [])[1];
    if (_q) localStorage.setItem("proton_puck_ab", _q);
    var _a = localStorage.getItem("proton_puck_ab");
    if (_a !== "popup" && _a !== "none") {
      _a = Math.random() < 0.5 ? "popup" : "none";
      localStorage.setItem("proton_puck_ab", _a);
    }
    CK_ARM = _a;
  } catch (er) { CK_ARM = "popup"; }
  function ensureArmTag(cart) {
    if (!CK_AB_LIVE) return Promise.resolve();
    if (cart && cart.attributes && cart.attributes.puck_ab === CK_ARM) return Promise.resolve();
    var req = fetch("/cart/update.js", {
      method: "POST", credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ attributes: { puck_ab: CK_ARM } }),
    }).catch(function () {});
    // never hold the shopper more than 1.5s for bookkeeping
    return Promise.race([req, new Promise(function (r) { setTimeout(r, 1500); })]);
  }

  function cartState(cart) {
    var st = { paddle: null, tee: null, cap: null, bag: null, bundle: false };
    (cart && cart.items || []).forEach(function (it) {
      if (it.handle === COYOTE) st.paddle = it;
      else if (it.handle === TEE) st.tee = it;
      else if (it.handle === CAP) st.cap = it;
      // 🎒 The bag is tracked so we never pitch it to someone who already has it — and so a
      // cart holding the tee AND the hat is no longer a dead end (it gets the Full Kit).
      else if (it.handle === BAG) st.bag = it;
      else if (BUNDLE_HANDLES.indexOf(it.handle) > -1) st.bundle = true;
    });
    return st;
  }
  function eligible(st) {
    if (!st.paddle || st.bundle) return false;
    // Only a cart that already holds every piece has nothing left to be offered.
    return !(st.tee && st.cap && st.bag);
  }
  function lineOptionValue(line, prod, nameRe) {
    if (!line) return null;
    var byName = (line.options_with_values || []).filter(function (o) { return nameRe.test(o.name || ""); })[0];
    if (byName && byName.value) return byName.value;
    if (!prod) return null;
    var idx = (prod.options || []).map(function (o) { return o.name || o; }).findIndex(function (n) { return nameRe.test(n); });
    if (idx < 0) return null;
    return (line.variant_options || [])[idx] || null;
  }
  // The bundle products and the standalone SKUs do not always spell an option value the
  // same way ("Safety Orange" on the kit vs "Orange" on the Cap). Resolve a carried-over
  // value against the bundle's own value list: exact → case-insensitive → substring.
  function resolveValue(values, want) {
    if (!want) return null;
    var w = String(want).toLowerCase();
    var hit = values.filter(function (v) { return v === want; })[0] ||
      values.filter(function (v) { return v.toLowerCase() === w; })[0] ||
      values.filter(function (v) { var lv = v.toLowerCase(); return lv.indexOf(w) > -1 || w.indexOf(lv) > -1; })[0];
    return hit || null;
  }

  // ONE renderer for both surfaces (drawer card + checkout modal) so the two can never
  // disagree about the same offer. Falls back to the old "$N together" wording whenever
  // the marginal figures could not be DERIVED — a missing live price must degrade to a
  // true statement, never to a guessed delta.
  function priceHtml(o, ns) {
    // 🚨 ONE WRAPPER, ALWAYS. `.pu-dy-card` is `display:flex;flex-direction:column`, so a
    //    bare <span> + <s> are TWO FLEX ITEMS and stack on separate lines — the numbers must
    //    share a single flex item to sit side by side. The fallback is wrapped too, so both
    //    branches produce the same child count and the card height never jumps.
    if (typeof o.addCents === "number" && typeof o.aloneCents === "number" && o.aloneCents > o.addCents) {
      return "<span class='" + ns + "-priceline'>" +
               "<span class='" + ns + "-price'>+" + money(o.addCents) + "</span>" +
               "<s class='" + ns + "-was'>" + money(o.aloneCents) + "</s>" +
             "</span>";
    }
    return "<span class='" + ns + "-priceline'><span class='" + ns + "-price'>" +
           money(o.priceCents) + " together</span></span>";
  }

  function offerRow(o, i) {
    var sels = o.selects.map(function (s) {
      var opts = s.values.map(function (v) { return "<option value='" + esc(v) + "'>" + esc(v) + "</option>"; }).join("");
      return "<select class='pu-sel' data-k='" + s.key + "' aria-label='" + esc(s.label) + "'>" + opts + "</select>";
    }).join("");
    return "<div class='pu-offer' data-i='" + i + "'>" +
      "<span class='pu-plate'><img src='" + esc(imgUrl(o.img, 160)) + "' alt=''></span>" +
      "<div><h4>" + esc(o.title) + "</h4>" +
      priceHtml(o, "pu-offer") +
      (o.less ? "<p class='pu-offer-less'>" + esc(o.less) + "</p>" : "<p class='pu-offer-less'></p>") +
      "<div class='pu-offer-row'>" + sels + "<button type='button' class='pu-go'>" + esc(o.cta) + "</button></div>" +
      "<p class='pu-err'></p></div></div>";
  }

  // Build the bundle offers for a cart state. Every offer swaps real cart lines for a
  // REAL bundle product, so each figure is the bundle's own live shelf price.
  // 🧠 MARGINAL PRICING (Diego, 2026-09-09): "instead of $215 together, put the smaller
  //    number of what adding the shirt would actually add to the total … and a slash of
  //    what it would be normally." A $215 figure beside a $195 cart reads as a NEW purchase;
  //    "+$20" reads as an upgrade. Same money, far lower friction.
  //
  //    🚨 BOTH NUMBERS ARE DERIVED, NEVER TYPED. `add` = the bundle's price minus the live
  //    price of every line the swap REMOVES; `alone` = the live standalone price of the
  //    piece(s) being gained. That makes "+$20" literally the cart delta the shopper will
  //    see, in every branch — including the ones where they already own the tee or the cap
  //    (there the paddle AND the tee come out, so the delta is only $10).
  //    ⭐ It stays consistent with the locked "$N less than buying them separately" line for
  //    free: (alone − add) === (parts − bundle) is the same subtraction rearranged.
  function priceOffer(o, bundle, removeProducts, gainedProducts) {
    var removed = 0, alone = 0, ok = true;
    (removeProducts || []).forEach(function (p) { if (p && typeof p.price === "number") removed += p.price; else ok = false; });
    (gainedProducts || []).forEach(function (p) { if (p && typeof p.price === "number") alone += p.price; else ok = false; });
    // A missing live price must NOT render a wrong delta — fall back to the bundle total.
    if (ok && bundle && typeof bundle.price === "number") {
      o.addCents = bundle.price - removed;
      o.aloneCents = alone;
    }
    return o;
  }

  function offersFor(st, prods) {
    var pad = prods.pad, tee = prods.tee, cap = prods.cap, bag = prods.bag;
    var bTee = prods.bTee, bCap = prods.bCap, bKit = prods.bKit, bBag = prods.bBag, bFull = prods.bFull;
    var offers = [];

    // 🚨 EVERY PUSH PRICES ITSELF. The old shape was `offers.push(x); if (offers.length)
    // priceOffer(offers[offers.length - 1], …)` — correct only while each push was the last
    // one and never skipped. Putting the bag card in front of the others made that false:
    // one unavailable bundle and the NEXT block re-priced the bag card with the tee's parts,
    // printing a delta that is not the delta the shopper would be charged. Pair them.
    function push(o, bundle, removed, gained) {
      priceOffer(o, bundle, removed, gained);
      offers.push(o);
      return o;
    }
    var sizeSel = function (b) { return { key: "size", label: "Tee size", values: optionValues(b, /size/i) }; };
    var colSel = function (b) { return { key: "colour", label: "Hat colour", values: optionValues(b, /col/i) }; };
    var sum = function (list) {
      var t = 0, ok = true;
      list.forEach(function (x) { if (x && typeof x.price === "number") t += x.price; else ok = false; });
      return ok ? t : null;
    };
    // The Full Kit carries the tee and the hat, so a size/colour the shopper already chose
    // must carry over silently; only what cannot be READ off the cart line is asked for.
    function fullKitOffer(title, removeHandles, gained) {
      if (!bFull || !bFull.available || !bag) return;
      var sz = st.tee ? resolveValue(optionValues(bFull, /size/i), lineOptionValue(st.tee, tee, /size/i)) : null;
      var cl = st.cap ? resolveValue(optionValues(bFull, /col/i), lineOptionValue(st.cap, cap, /col/i)) : null;
      var fixed = [], sels = [];
      if (sz) fixed.push(sz); else sels.push(sizeSel(bFull));
      if (cl) fixed.push(cl); else sels.push(colSel(bFull));
      var parts = sum([pad, tee, cap, bag]);
      push({
        key: "full", title: title, img: bFull.featured_image, pieceImg: bag.featured_image,
        priceCents: bFull.price, less: parts != null ? lessLine(bFull.price, parts) : "",
        bundle: bFull, fixed: fixed, selects: sels,
        removeHandles: removeHandles, cta: "Add & check out"
      }, bFull, [pad].concat(removeHandles.indexOf(TEE) > -1 ? [tee] : [], removeHandles.indexOf(CAP) > -1 ? [cap] : []), gained);
    }

    if (!st.tee && !st.cap) {
      // 🎒 THE BAG GOES FIRST (Diego 2026-09-22). It is the biggest attach on the ladder
      // (+$80 on a $195 cart), it is the piece a paddle buyer actually needs, and the swap is
      // pickerless — one tap. `selects: []` is deliberate: matchVariant(bundle, []) returns
      // the kit's single variant, so no dropdown is rendered and none is required.
      if (!st.bag && bBag && bBag.available && bag)
        push({
          key: "bag", title: "Add the Backpack \u2014 the Kit", img: bBag.featured_image, pieceImg: bag.featured_image,
          priceCents: bBag.price, less: pad && bag ? lessLine(bBag.price, pad.price + bag.price) : "",
          bundle: bBag, fixed: [], selects: [],
          removeHandles: [COYOTE], cta: "Add & check out"
        }, bBag, [pad], [bag]);

      if (bTee && bTee.available && tee)
        push({
          key: "tee", title: "Add the Tee", img: bTee.featured_image, pieceImg: tee.featured_image,
          priceCents: bTee.price, less: pad ? lessLine(bTee.price, pad.price + tee.price) : "",
          bundle: bTee, selects: [sizeSel(bTee)],
          removeHandles: [COYOTE], cta: "Add & check out"
        }, bTee, [pad], [tee]);

      if (bCap && bCap.available && cap)
        push({
          key: "cap", title: "Add the Hat", img: bCap.featured_image, pieceImg: cap.featured_image,
          priceCents: bCap.price, less: pad ? lessLine(bCap.price, pad.price + cap.price) : "",
          bundle: bCap, selects: [colSel(bCap)],
          removeHandles: [COYOTE], cta: "Add & check out"
        }, bCap, [pad], [cap]);

      if (bKit && bKit.available && tee && cap)
        push({
          key: "kit", title: "Add both \u2014 the Kit", img: bKit.featured_image, pieceImg: bKit.featured_image,
          priceCents: bKit.price, less: pad ? lessLine(bKit.price, pad.price + tee.price + cap.price) : "",
          bundle: bKit, selects: [sizeSel(bKit), colSel(bKit)],
          removeHandles: [COYOTE], cta: "Add & check out"
        }, bKit, [pad], [tee, cap]);

    } else if (st.tee && !st.cap) {
      if (bKit && bKit.available) {
        var sz = resolveValue(optionValues(bKit, /size/i), lineOptionValue(st.tee, tee, /size/i));
        var selsB = [colSel(bKit)];
        if (!sz) selsB.unshift(sizeSel(bKit)); // size unreadable → ask
        push({
          key: "kit", title: "Add the Hat \u2014 complete the Kit", img: bKit.featured_image, pieceImg: cap ? cap.featured_image : bKit.featured_image,
          priceCents: bKit.price, less: pad && tee && cap ? lessLine(bKit.price, pad.price + tee.price + cap.price) : "",
          bundle: bKit, fixed: sz ? [sz] : [], selects: selsB,
          removeHandles: [COYOTE, TEE], cta: "Add & check out"
        }, bKit, [pad, tee], [cap]);
      }
      if (!st.bag) fullKitOffer("Add the Hat + Backpack \u2014 the Full Kit", [COYOTE, TEE], [cap, bag]);

    } else if (st.cap && !st.tee) {
      if (bKit && bKit.available) {
        var cl = resolveValue(optionValues(bKit, /col/i), lineOptionValue(st.cap, cap, /col/i));
        var selsC = [sizeSel(bKit)];
        if (!cl) selsC.push(colSel(bKit)); // colour unreadable → ask
        push({
          key: "kit", title: "Add the Tee \u2014 complete the Kit", img: bKit.featured_image, pieceImg: tee ? tee.featured_image : bKit.featured_image,
          priceCents: bKit.price, less: pad && tee && cap ? lessLine(bKit.price, pad.price + tee.price + cap.price) : "",
          bundle: bKit, fixed: cl ? [cl] : [], selects: selsC,
          removeHandles: [COYOTE, CAP], cta: "Add & check out"
        }, bKit, [pad, cap], [tee]);
      }
      if (!st.bag) fullKitOffer("Add the Tee + Backpack \u2014 the Full Kit", [COYOTE, CAP], [tee, bag]);

    } else if (st.tee && st.cap && !st.bag) {
      // 🆕 Used to be a dead end (`eligible()` returned false and the shopper went straight
      // through). The tee and the hat are already theirs — the only piece left is the bag,
      // and the Full Kit is where it lives.
      fullKitOffer("Add the Backpack \u2014 the Full Kit", [COYOTE, TEE, CAP], [bag]);
    }
    return offers;
  }

  // Swap: add the bundle variant, then decrement the component lines it replaces.
  // Lines are resolved FRESH at click time (the render-time cart may be stale or DOM-derived).
  function performSwap(o, v) {
    return getCartRetry(2).then(function (cart) {
      if (!cart) throw new Error("cart unavailable");
      return addToCart([{ id: v.id, quantity: 1 }]).then(function () {
        var chain = Promise.resolve();
        (o.removeHandles || []).forEach(function (h) {
          var line = (cart.items || []).filter(function (it) { return it.handle === h; })[0];
          if (!line) return;
          chain = chain.then(function () {
            return changeLine(line.key, Math.max(0, line.quantity - 1)).catch(function () {
              return changeLine(line.key, Math.max(0, line.quantity - 1)); // one retry
            });
          });
        });
        return chain;
      });
    });
  }

  function showModal(st, prods) {
    var offers = offersFor(st, prods);
    if (!offers.length) { go(); return; }

    injectCSS();
    var ov = document.createElement("div");
    ov.className = "pu-ov";
    ov.id = "pu-ck";
    ov.innerHTML = "<div class='pu-md' role='dialog' aria-modal='true' aria-label='Coyote Collection offer'>" +
      "<button type='button' class='pu-x' aria-label='Close'>&times;</button>" +
      "<p class='pu-md-eyebrow'>Before you check out</p>" +
      "<h3>Complete the Coyote kit</h3>" +
      offers.map(offerRow).join("") +
      "<button type='button' class='pu-skip'>No thanks &mdash; continue to checkout</button></div>";
    document.body.appendChild(ov);
    // Inline hide of the /cart sticky bar (see the CSS comment above the :has rule) — the
    // bar's own logic only toggles a class, never style.display, so this cannot be fought.
    var stickyBar = document.getElementById("pp-sticky-checkout");
    if (stickyBar) stickyBar.style.setProperty("display", "none", "important");
    requestAnimationFrame(function () { ov.classList.add("is-on"); });

    function close(deliberate) {
      if (deliberate) try { sessionStorage.setItem(SEEN_KEY, "1"); } catch (e) {}
      ov.classList.remove("is-on");
      if (stickyBar) stickyBar.style.removeProperty("display");
      setTimeout(function () { ov.remove(); }, 240);
    }
    ov.addEventListener("click", function (e) {
      if (e.target === ov || e.target.closest(".pu-x")) { close(true); return; }
      if (e.target.closest(".pu-skip")) { try { sessionStorage.setItem(SEEN_KEY, "1"); } catch (er) {} go(); return; }
      var btn = e.target.closest(".pu-go");
      if (!btn || btn.disabled) return;
      var row = btn.closest(".pu-offer");
      var o = offers[Number(row.getAttribute("data-i"))];
      var wants = (o.fixed || []).slice();
      row.querySelectorAll(".pu-sel").forEach(function (s) { wants.push(s.value); });
      var v = matchVariant(o.bundle, wants);
      var err = row.querySelector(".pu-err");
      if (!v || !v.available) {
        err.style.display = "block";
        err.textContent = "That combination is unavailable right now — pick another.";
        return;
      }
      err.style.display = "none";
      btn.disabled = true;
      btn.textContent = "Adding…";
      performSwap(o, v).then(function () {
        go();
      }).catch(function () {
        btn.disabled = false;
        btn.textContent = o.cta;
        err.style.display = "block";
        err.textContent = "Couldn’t update the cart — try again, or continue to checkout.";
      });
    });
  }

  function go() {
    proceeding = true;
    // skip_shop_pay=true skips Shopify's shop.app "universal redirect" bounce page,
    // which is a hard dead end ("Shop Blocked") for shoppers running ad-blockers or
    // privacy DNS. Shop Pay itself still works INSIDE checkout. Diego-approved 09-10.
    window.location.href = "/checkout?skip_shop_pay=true";
  }

  function bindCheckoutIntercept() {
    document.addEventListener("click", function (e) {
      if (proceeding) return;
      var btn = e.target.closest && e.target.closest("button[name='checkout'],input[name='checkout']");
      if (!btn) return;
      var seen = false;
      try { seen = sessionStorage.getItem(SEEN_KEY) === "1"; } catch (er) {}
      if (seen) return;
      if (document.getElementById("pu-ck")) { e.preventDefault(); e.stopPropagation(); return; }
      e.preventDefault();
      e.stopPropagation();
      getCart().then(function (cart) {
        return ensureArmTag(cart).then(function () { return cart; });
      }).then(function (cart) {
        var st = cartState(cart);
        if (!cart || !eligible(st)) { go(); return; }
        if (CK_AB_LIVE && CK_ARM === "none") { go(); return; }
        Promise.all([product(COYOTE), product(TEE), product(CAP), product(B_TEE), product(B_CAP), product(B_KIT),
                     product(BAG), product(B_BAG), product(B_FULL)])
          .then(function (r) {
            showModal(st, { pad: r[0], tee: r[1], cap: r[2], bTee: r[3], bCap: r[4], bKit: r[5],
                            bag: r[6], bBag: r[7], bFull: r[8] });
          });
      }).catch(function () { go(); });
    }, true);
  }

  /* ------------------------------------------------------------- boot */
  function boot() {
    injectCSS();
    // tag a non-empty cart with its A/B arm on every page view (covers express-pay paths too)
    getCart().then(function (c) { if (c && c.item_count > 0) ensureArmTag(c); }).catch(function () {});
    bindCheckoutIntercept(); // every page — the drawer / cart-notification "Check out" appears everywhere
    initDrawerRail(); // "You may also like" quick-adds inside the cart drawer, on every page

    var handle = currentHandle();
    if (handle && isPaddlePDP()) {
      var media = document.querySelector(".product__media-wrapper");
      var info = document.querySelector(".product__info-wrapper");
      // Placement history (BOTH Diego, 2026-09-10 — the LATER one wins):
      //  am: both rows below the product section at full page width.
      //  pm: "Really liked when you had this area in this blank space. Please put
      //      it back." → the COYOTE TEASER returns to the media column under the
      //      gallery on DESKTOP (it fills the otherwise-blank left column). Mobile
      //      keeps the below-section slot (the media column sits above the buy box
      //      there). The GEAR MARQUEE stays below the section on every breakpoint.
      var parent = document.createElement("div");
      parent.className = "page-width";
      var sec = (info || media);
      var host = sec ? sec.closest(".shopify-section") || sec.parentElement : null;
      if (host && host.parentElement) host.parentElement.insertBefore(parent, host.nextSibling);
      else if (document.querySelector("main")) document.querySelector("main").appendChild(parent);
      else return;
      // Fixed slots so async fetches can never reorder the blocks: teaser first, marquee under it.
      var teaserHost = document.createElement("div");
      var railHost = document.createElement("div");
      parent.appendChild(teaserHost);
      parent.appendChild(railHost);
      if (handle === COYOTE) {
        var desktopMediaMount = media && window.matchMedia("(min-width: 990px)").matches;
        if (desktopMediaMount) {
          var underGallery = document.createElement("div");
          media.appendChild(underGallery);
          buildCoyoteTeaser(underGallery);
        } else {
          buildCoyoteTeaser(teaserHost);
        }
      }
      buildMarquee(railHost, handle);
    }
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
})();
